/**
 * Localization form: submits on radio change. Persists `watkins_country` in localStorage.
 * On load, dropdown reflects `watkins_country` when that option exists and is visible.
 *
 * URL `?country=US` / `?country=CA`: sets market + custom-language-picker flag (USD / CAD),
 * persists choice, and POSTs `/localization` when Shopify market does not match.
 *
 * Guests (logged out) and logged-in customers with no default address:
 * - Default market from IP via browsing_context_suggestions.json (or consultant-link context).
 * - Both US and CA stay visible; shopper may switch freely.
 * - Manual picks set `watkins_country` + `watkins_country_user_override` so IP does not overwrite them.
 *
 * Logged in with a US/CA default address:
 * - Market locked to that country; `.custom-language-picker` cannot change it.
 * - One POST to `/localization` when Shopify market ≠ default-address country.
 *
 * Consultant link (`watkins_consultantSource` = url) while guest: first pick follows consultant US/CA;
 * after the shopper changes the control, their choice sticks until a different consultant id loads.
 *
 * Debug: set `window.__watkinsDebugCountry = true` or `DEBUG_ALERT_RESOLVED_COUNTRY = true`.
 *
 * Country-specific page blocks: `data-detected-country="US"|"CA"` via
 * `WatkinsLocalizationCountryContent.applyDetectedCountryContent()`.
 */

const WatkinsLocalizationConfig = Object.freeze({
  DEBUG_ALERT_RESOLVED_COUNTRY: false,
  STORAGE_KEY_WATKINS_CONSULTANT: 'watkins_consultant',
  STORAGE_KEY_WATKINS_CONSULTANT_ID: 'watkins_consultantID',
  STORAGE_KEY_WATKINS_CONSULTANT_SOURCE: 'watkins_consultantSource',
  STORAGE_KEY_WATKINS_COUNTRY: 'watkins_country',
  /** Guest (or consultant-url) manual US/CA pick — resolution must not snap back to IP/consultant. */
  STORAGE_KEY_WATKINS_COUNTRY_USER_OVERRIDE: 'watkins_country_user_override',
  EVENT_WATKINS_CONSULTANT_STORAGE: 'watkins:consultant-storage-updated',
  EVENT_WATKINS_COUNTRY_UPDATED: 'watkins:watkins-country-updated',
  /** Hide non-matching `[data-detected-country]` blocks (inline `display` wins over theme CSS). */
  CLASS_DETECTED_COUNTRY_HIDDEN: 'watkins-detected-country-hidden',
  SELECTOR_FORM: 'form.localization-form',
  SELECTOR_CUSTOM_SELECT: 'custom-select',
  SELECTOR_COUNTRY_RADIOS: 'input.country-option[name="country_code"]',
  SELECTOR_CUSTOM_SELECT_TOGGLE: '[data-toggle="options"]',
  SELECTOR_OPTION_LABEL_TEXT: '.option_txt',
  CLASS_COUNTRY_DETECT_US: 'country-detect-US',
  CLASS_COUNTRY_DETECT_CA: 'country-detect-CA',
  /** Matches `custom-language-picker` market control; avoids flicker while async filter runs. */
  CLASS_CUSTOM_CURRENCY_DROPDOWN: 'custom-currency-dropdown',
  /** Set on `<custom-select>` after first market filter pass (see `global-header.css`). */
  ATTR_MARKET_UI_READY: 'data-watkins-market-ui-ready',
  /** Logged-in: market picker is display-only (cannot open / change). */
  ATTR_MARKET_LOCKED: 'data-watkins-market-locked',
  CLASS_MARKET_PICKER_LOCKED: 'watkins-market-picker--locked',
  /** Hides LIs; global-header uses `li { display: flex !important }` on the market list — class + CSS wins. */
  CLASS_COUNTRY_ROW_HIDDEN: 'watkins-market-row--hidden',
  ISO_CODES_US: new Set(['US', 'USA', 'U.S.A.', 'U.S.', 'UNITED STATES']),
  ISO_CODES_CA: new Set(['CA', 'CAN', 'CANADA']),
  /** Tracks last consultant id per tab so a *different* `watkins_consultantID` clears manual market override. */
  SESSION_KEY_LAST_CONSULTANT_ID_FOR_MARKET: 'watkins_last_consultant_id_for_market',
});

/**
 * Toggle labels, list visibility, and currency dropdown readiness for the market picker.
 * @class
 */
class WatkinsLocalizationDom {
  /**
   * Keeps `country-detect-US` / `country-detect-CA` on the visible toggle label in sync with selection.
   * @param {HTMLElement | null} optionTextEl
   * @param {string} countryIso e.g. from `HTMLInputElement.value`
   */
  static syncCountryDetectClassesOnOptionText(optionTextEl, countryIso) {
    if (!(optionTextEl instanceof HTMLElement)) return;
    const iso = String(countryIso || '')
      .trim()
      .toUpperCase();
    optionTextEl.classList.remove(
      WatkinsLocalizationConfig.CLASS_COUNTRY_DETECT_US,
      WatkinsLocalizationConfig.CLASS_COUNTRY_DETECT_CA
    );
    if (iso === 'CA') {
      optionTextEl.classList.add(WatkinsLocalizationConfig.CLASS_COUNTRY_DETECT_CA);
    } else if (iso === 'US') {
      optionTextEl.classList.add(WatkinsLocalizationConfig.CLASS_COUNTRY_DETECT_US);
    }
  }

  /**
   * Reveals `.custom-currency-dropdown` toggle after hydration (first market filter pass).
   * @param {{ _customSelect?: Element | null }} formEl
   */
  static setCurrencyDropdownMarketUiReady(formEl) {
    const cs = formEl._customSelect;
    if (!cs || !cs.classList.contains(WatkinsLocalizationConfig.CLASS_CUSTOM_CURRENCY_DROPDOWN)) return;
    try {
      cs.setAttribute(WatkinsLocalizationConfig.ATTR_MARKET_UI_READY, '');
    } catch {
      /* */
    }
  }

  /**
   * @param {HTMLElement} listItem
   * @param {boolean} isVisible
   */
  static setCountryListItemVisible(listItem, isVisible) {
    if (isVisible) {
      listItem.classList.remove(WatkinsLocalizationConfig.CLASS_COUNTRY_ROW_HIDDEN);
      listItem.removeAttribute('hidden');
      listItem.removeAttribute('aria-hidden');
      listItem.style.removeProperty('display');
    } else {
      listItem.classList.add(WatkinsLocalizationConfig.CLASS_COUNTRY_ROW_HIDDEN);
      listItem.setAttribute('hidden', '');
      listItem.setAttribute('aria-hidden', 'true');
    }
  }

  /**
   * Lock / unlock `.custom-language-picker` so logged-in shoppers cannot change market.
   * @param {{ _customSelect?: Element | null, _form?: HTMLFormElement | null }} formEl
   * @param {boolean} locked
   */
  static setMarketPickerLocked(formEl, locked) {
    const cs = formEl?._customSelect;
    const form = formEl?._form;
    const picker = cs?.closest?.('.custom-language-picker') || form?.closest?.('.custom-language-picker');
    if (picker instanceof HTMLElement) {
      picker.classList.toggle(WatkinsLocalizationConfig.CLASS_MARKET_PICKER_LOCKED, locked);
    }
    if (cs instanceof HTMLElement) {
      if (locked) {
        cs.setAttribute(WatkinsLocalizationConfig.ATTR_MARKET_LOCKED, '');
        cs.classList.remove('open');
      } else {
        cs.removeAttribute(WatkinsLocalizationConfig.ATTR_MARKET_LOCKED);
      }
    }
    const toggle = cs?.querySelector?.(WatkinsLocalizationConfig.SELECTOR_CUSTOM_SELECT_TOGGLE);
    if (toggle instanceof HTMLElement) {
      if (locked) {
        toggle.setAttribute('disabled', '');
        toggle.setAttribute('aria-disabled', 'true');
        toggle.setAttribute('aria-expanded', 'false');
        toggle.setAttribute('tabindex', '-1');
      } else {
        toggle.removeAttribute('disabled');
        toggle.removeAttribute('aria-disabled');
        toggle.removeAttribute('tabindex');
      }
    }
    const panel = cs?.querySelector?.('[data-selectcontainer]');
    if (panel instanceof HTMLElement && locked) {
      panel.setAttribute('aria-hidden', 'true');
      panel.setAttribute('inert', '');
    }
  }
}

/**
 * `[data-detected-country]`, `watkins_country` storage helpers, and `submitted_from` sync.
 * @class
 */
class WatkinsLocalizationCountryContent {
  /**
   * Active market for `[data-detected-country]` content: `CA` only when `watkins_country` is CA; otherwise `US`.
   * @returns {'US' | 'CA'}
   */
  static getActiveWatkinsCountryForDetectedContent() {
    let raw;
    try {
      raw = globalThis.localStorage.getItem(WatkinsLocalizationConfig.STORAGE_KEY_WATKINS_COUNTRY);
    } catch {
      return 'US';
    }
    if (raw && String(raw).trim().toUpperCase() === 'CA') return 'CA';
    return 'US';
  }

  /**
   * Sets every `input[name="submitted_from"]` to the current `watkins_country` market (`US` or `CA`; default `US`).
   */
  static syncAllSubmittedFromFieldsFromWatkinsCountry() {
    const v = WatkinsLocalizationCountryContent.getActiveWatkinsCountryForDetectedContent();
    try {
      document.querySelectorAll('input[name="submitted_from"]').forEach((inp) => {
        if (inp instanceof HTMLInputElement) inp.value = v;
      });
    } catch {
      /* */
    }
  }

  /**
   * @param {HTMLFormElement} form
   */
  static syncSubmittedFromFieldsInFormFromWatkinsCountry(form) {
    if (!(form instanceof HTMLFormElement)) return;
    const v = WatkinsLocalizationCountryContent.getActiveWatkinsCountryForDetectedContent();
    try {
      form.querySelectorAll('input[name="submitted_from"]').forEach((inp) => {
        if (inp instanceof HTMLInputElement) inp.value = v;
      });
    } catch {
      /* */
    }
  }

  /**
   * Shows elements whose `data-detected-country` matches `watkins_country` (CA), or US when missing/invalid.
   * Hides the other (US vs CA). Run on every page; call again after market change or `shopify:section:load`.
   */
  static applyDetectedCountryContent() {
    const active = WatkinsLocalizationCountryContent.getActiveWatkinsCountryForDetectedContent();
    try {
      document.documentElement.setAttribute('data-watkins-active-country', active);
    } catch {
      /* */
    }
    let list;
    try {
      list = document.querySelectorAll('[data-detected-country]');
    } catch {
      return;
    }
    list.forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const v = (el.getAttribute('data-detected-country') || '').trim().toUpperCase();
      if (v !== 'US' && v !== 'CA') return;
      const matches = v === active;
      if (matches) {
        el.classList.remove(WatkinsLocalizationConfig.CLASS_DETECTED_COUNTRY_HIDDEN);
        el.removeAttribute('hidden');
        el.removeAttribute('aria-hidden');
        el.style.removeProperty('display');
      } else {
        el.classList.add(WatkinsLocalizationConfig.CLASS_DETECTED_COUNTRY_HIDDEN);
        el.setAttribute('hidden', '');
        el.setAttribute('aria-hidden', 'true');
        el.style.setProperty('display', 'none', 'important');
      }
    });
  }

  static bootDetectedCountryContent() {
    const run = () => {
      WatkinsLocalizationCountryContent.applyDetectedCountryContent();
      WatkinsLocalizationCountryContent.syncAllSubmittedFromFieldsFromWatkinsCountry();
    };
    run();
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', run, { once: true });
    }
    document.addEventListener(WatkinsLocalizationConfig.EVENT_WATKINS_COUNTRY_UPDATED, run);
    globalThis.addEventListener('storage', (e) => {
      if (e.key === WatkinsLocalizationConfig.STORAGE_KEY_WATKINS_COUNTRY) run();
    });
    document.addEventListener('shopify:section:load', run);
    setTimeout(run, 0);
    document.addEventListener(
      'submit',
      (e) => {
        const t = e.target;
        if (t instanceof HTMLFormElement && t.querySelector('input[name="submitted_from"]')) {
          WatkinsLocalizationCountryContent.syncSubmittedFromFieldsInFormFromWatkinsCountry(t);
        }
      },
      true
    );
  }
}

/**
 * Consultant URL context, browsing-context fetch, US/CA resolution, and `watkins_country` storage.
 * @class
 */
class WatkinsLocalizationMarket {
  /** @type {Promise<string|null>|null} */
  static _browsingContextCountryPromise = null;

  /** Set synchronously so parallel async runs do not all pass the check (race on `window`). */
  static _hasShownWatkinsCountryDebugAlert = false;

  /** One auto POST to `/localization` per page (header + mobile share the same need). */
  static _hasAutoSubmittedMarketMisfit = false;

  static get hasAutoSubmittedMarketMisfit() {
    return WatkinsLocalizationMarket._hasAutoSubmittedMarketMisfit;
  }

  static set hasAutoSubmittedMarketMisfit(v) {
    WatkinsLocalizationMarket._hasAutoSubmittedMarketMisfit = Boolean(v);
  }

  /**
   * True when this page renders a dynamic checkout / "Buy it now" button
   * (`{{ form | payment_button }}`, which Shopify wraps in
   * `.shopify-payment-button`). Some of those payment methods build a
   * checkout directly from the product on click without ever populating the
   * persistent session cart — there is nothing to snapshot in that case, so
   * the auto market-switch submit (see call sites below) must not fire at
   * all on a page where the shopper could be mid-click on one of these,
   * rather than relying on `submitMarketFormPreservingCart()`'s snapshot to
   * catch it after the fact. The market misfit simply gets corrected on the
   * next page load that doesn't have this button instead.
   * @returns {boolean}
   */
  static pageHasDynamicCheckoutButton() {
    try {
      return Boolean(document.querySelector('.shopify-payment-button'));
    } catch {
      return false;
    }
  }

  /**
   * True while an add-to-cart submit (component-product-form.js,
   * component-quick-order.js) is in flight — set synchronously the instant a
   * shopper clicks Add to Cart / Buy Now, before any `await`. Checked below
   * so the market-switch submit never fires in the middle of one.
   * @returns {boolean}
   */
  static isAddToCartInFlight() {
    try {
      return Boolean(globalThis.Utility?.isAddToCartInFlight?.());
    } catch {
      return false;
    }
  }

  /**
   * Submits the `/localization` market-switch form. Switching market (e.g.
   * US <-> CA here) changes presentment currency, which is a different
   * Shopify Markets price list — the platform empties the cart as a side
   * effect of that switch, and there's no way to prevent that server-side
   * behavior from the theme.
   *
   * An add-to-cart click can land in the ~100ms window before this fires
   * (see call sites), so this checks `isAddToCartInFlight()` twice: once up
   * front, and again right before the actual `form.submit()` in case a click
   * happened while the `/cart.js` read below was in flight. If either check
   * finds one in progress, the switch is deferred to a later page load
   * rather than risk yanking the cart out from under the add-to-cart request
   * (which itself has no way to know its result is about to be discarded).
   *
   * As a second line of defense — for anything that reaches the cart without
   * going through markAddToCartStart()/markAddToCartEnd() — whatever is
   * already in the cart right before submitting is handed to
   * Utility.Cart.rememberAddedItems() (utility.js), which stashes it in
   * sessionStorage (survives the reload, tab-scoped). On the page that loads
   * after the market switch, Utility.Cart.restoreMissingCartAdditions() —
   * the single general restore path used for every cause of a cart-emptying
   * reload, not just this one — re-adds those exact lines (including any
   * Points/Bonus properties they already had) via `/cart/add.js`.
   * @param {HTMLFormElement | null | undefined} form
   */
  static async submitMarketFormPreservingCart(form) {
    if (!form) return;

    if (WatkinsLocalizationMarket.isAddToCartInFlight()) {
      WatkinsLocalizationMarket.hasAutoSubmittedMarketMisfit = false;
      return;
    }

    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (response.ok) {
        const cart = await response.json();
        if (cart && Array.isArray(cart.items) && cart.items.length > 0) {
          WatkinsLocalizationMarket._saveCartForRestoreAcrossMarketSwitch(cart.items);
        }
      }
    } catch {
      // If the read fails, there's nothing to stash — proceed with the
      // switch as before rather than getting stuck permanently out of sync.
    }

    if (WatkinsLocalizationMarket.isAddToCartInFlight()) {
      WatkinsLocalizationMarket.hasAutoSubmittedMarketMisfit = false;
      return;
    }

    form.submit();
  }

  /**
   * @param {Array<{variant_id?: number, id?: number, quantity: number, properties?: Record<string, string>}>} items
   */
  static _saveCartForRestoreAcrossMarketSwitch(items) {
    // Delegates to the general Utility.Cart safety net (utility.js) rather
    // than keeping a second, separate stash/restore pair: two independent
    // mechanisms both capable of re-adding the same item on the same page
    // load was causing it to be added twice. There is now exactly one
    // stash and one restore path (Utility.Cart.restoreMissingCartAdditions,
    // called once at boot below), for every cause of a cart-emptying reload.
    try {
      globalThis.Utility?.Cart?.rememberAddedItems?.(items);
    } catch {
      // sessionStorage unavailable (private mode, quota, etc.) — the cart
      // will still be emptied by the market switch, just not restorable.
    }
  }

  /**
   * @returns {'US' | 'CA' | null} null = show all country options
   */
  static getWatkinsCountryFromStorage() {
    try {
      const v = window.localStorage.getItem(WatkinsLocalizationConfig.STORAGE_KEY_WATKINS_COUNTRY);
      if (!v || !String(v).trim()) return null;
      return String(v).trim().toUpperCase();
    } catch {
      return null;
    }
  }

  /**
   * @param {string} iso
   */
  static setWatkinsCountryInStorage(iso) {
    if (!iso) return;
    try {
      window.localStorage.setItem(
        WatkinsLocalizationConfig.STORAGE_KEY_WATKINS_COUNTRY,
        String(iso).trim().toUpperCase()
      );
    } catch {
      /* quota / private mode */
    }
    try {
      document.dispatchEvent(
        new CustomEvent(WatkinsLocalizationConfig.EVENT_WATKINS_COUNTRY_UPDATED, {
          detail: { iso: String(iso).trim().toUpperCase() },
        })
      );
    } catch {
      /* */
    }
    WatkinsLocalizationCountryContent.applyDetectedCountryContent();
    WatkinsLocalizationCountryContent.syncAllSubmittedFromFieldsFromWatkinsCountry();
  }

  /**
   * @param {unknown} raw Country handle / ISO from API, customer, etc.
   * @returns {'US' | 'CA' | null} null => show both US/CA (other or unknown)
   */
  static mapCountryToUsCaOrAll(raw) {
    if (raw == null || !String(raw).trim()) return null;
    const s = String(raw).trim().toUpperCase();
    if (s === 'US' || WatkinsLocalizationConfig.ISO_CODES_US.has(s)) return 'US';
    if (s === 'CA' || WatkinsLocalizationConfig.ISO_CODES_CA.has(s)) return 'CA';
    return null;
  }

  /**
   * Valid `?country=` / `?country_code=` on the current URL (`US` or `CA`).
   * Drives custom-language-picker USD/CAD flag and Shopify market sync.
   * @returns {'US' | 'CA' | null}
   */
  static getCountryFromUrlQuery() {
    try {
      const params = new globalThis.URLSearchParams(globalThis.location.search);
      const raw = params.get('country') ?? params.get('country_code');
      return WatkinsLocalizationMarket.mapCountryToUsCaOrAll(raw);
    } catch {
      return null;
    }
  }

  /**
   * Valid ?consultant= / consultantId / consultant_id on the current URL.
   * @returns {boolean}
   */
  static urlHasConsultantQuery() {
    try {
      const p = new globalThis.URLSearchParams(globalThis.location.search);
      return ['consultant', 'consultantId', 'consultant_id'].some((k) => {
        const v = p.get(k);
        return v != null && String(v).trim() !== '';
      });
    } catch {
      return false;
    }
  }

  /**
   * Shop-with-consultant link: URL param and/or `watkins_consultantSource` from check-consultant.js.
   * @returns {boolean}
   */
  static isConsultantUrlShoppingContext() {
    try {
      if (
        globalThis.localStorage.getItem(WatkinsLocalizationConfig.STORAGE_KEY_WATKINS_CONSULTANT_SOURCE) === 'url'
      )
        return true;
    } catch {
      /* */
    }
    return WatkinsLocalizationMarket.urlHasConsultantQuery();
  }

  /**
   * `watkins_consultantSource === 'url'` from check-consultant.js.
   * @returns {boolean}
   */
  static isConsultantUrlSource() {
    try {
      return globalThis.localStorage.getItem(WatkinsLocalizationConfig.STORAGE_KEY_WATKINS_CONSULTANT_SOURCE) === 'url';
    } catch {
      return false;
    }
  }

  static getStoredConsultantMarketIso() {
    let json;
    try {
      json = window.localStorage.getItem(WatkinsLocalizationConfig.STORAGE_KEY_WATKINS_CONSULTANT);
    } catch {
      return null;
    }
    if (!json?.trim()) return null;

    let consultant;
    try {
      consultant = JSON.parse(json);
    } catch {
      return null;
    }
    if (!consultant || typeof consultant !== 'object' || Array.isArray(consultant)) {
      return null;
    }

    const addr = consultant.address;
    const fromNestedAddress =
      addr && typeof addr === 'object' && !Array.isArray(addr)
        ? addr.country ||
          addr.countryCode ||
          addr.country_code ||
          addr.countryIso ||
          addr.country_iso
        : null;

    const raw =
      consultant.country ??
      consultant.countryCode ??
      consultant.country_code ??
      consultant.CountryCode ??
      consultant.countryIso ??
      consultant.country_iso ??
      consultant.addressCountry ??
      consultant.market ??
      fromNestedAddress;

    if (raw == null || !String(raw).trim()) return null;
    const normalized = String(raw).trim().toUpperCase();
    if (WatkinsLocalizationConfig.ISO_CODES_US.has(normalized)) return 'US';
    if (WatkinsLocalizationConfig.ISO_CODES_CA.has(normalized)) return 'CA';
    return null;
  }

  /**
   * Logged-in customer default address country from theme.liquid (`customer.default_address.country_code` only).
   * Does not fall back to current Shopify market / IP.
   * @returns {'US' | 'CA' | null}
   */
  static getCustomerDefaultAddressMarketIso() {
    if (typeof globalThis === 'undefined') return null;
    if (globalThis.__watkinsCustomerLoggedIn !== true) return null;
    if (globalThis.__watkinsCustomerHasDefaultAddress === false) return null;
    const raw = globalThis.__watkinsCustomerCountryIso;
    if (raw == null || !String(raw).trim()) return null;
    return WatkinsLocalizationMarket.mapCountryToUsCaOrAll(raw);
  }

  /**
   * Logged-in customer with orders: US/CA from latest order address (theme.liquid).
   * Used only when default address is missing or not US/CA.
   * @returns {'US' | 'CA' | null}
   */
  static getCustomerOrderAddressMarketIso() {
    if (typeof globalThis === 'undefined') return null;
    if (globalThis.__watkinsCustomerLoggedIn !== true) return null;
    if (globalThis.__watkinsCustomerHasOrders !== true) return null;
    const raw = globalThis.__watkinsCustomerOrderCountryIso;
    if (raw == null || !String(raw).trim()) return null;
    return WatkinsLocalizationMarket.mapCountryToUsCaOrAll(raw);
  }

  /**
   * Guest manually picked US/CA; IP / auto-resolve must not overwrite.
   * Logged-in with a US/CA default address cannot override; without an address they may switch freely.
   * @returns {boolean}
   */
  static guestMarketUserOverrideActive() {
    const lockedIso = WatkinsLocalizationMarket.getCustomerDefaultAddressMarketIso();
    if (lockedIso === 'US' || lockedIso === 'CA') return false;
    try {
      return (
        globalThis.localStorage.getItem(WatkinsLocalizationConfig.STORAGE_KEY_WATKINS_COUNTRY_USER_OVERRIDE) === 'true'
      );
    } catch {
      return false;
    }
  }

  /**
   * Shopper manually picked US/CA during a consultant-url session; resolution/UI must not revert to consultant.
   * @returns {boolean}
   */
  static consultantUrlMarketUserOverrideActive() {
    try {
      return (
        globalThis.localStorage.getItem(WatkinsLocalizationConfig.STORAGE_KEY_WATKINS_COUNTRY_USER_OVERRIDE) === 'true'
      );
    } catch {
      return false;
    }
  }

  static clearMarketUserOverride() {
    try {
      globalThis.localStorage.removeItem(WatkinsLocalizationConfig.STORAGE_KEY_WATKINS_COUNTRY_USER_OVERRIDE);
    } catch {
      /* */
    }
  }

  static setMarketUserOverride() {
    try {
      globalThis.localStorage.setItem(WatkinsLocalizationConfig.STORAGE_KEY_WATKINS_COUNTRY_USER_OVERRIDE, 'true');
    } catch {
      /* */
    }
  }

  /**
   * Associated US/CA from the logged-in customer's default address only.
   * @returns {'US' | 'CA' | null}
   */
  static getLoggedInAssociatedMarketIso() {
    if (typeof globalThis === 'undefined' || globalThis.__watkinsCustomerLoggedIn !== true) {
      return null;
    }
    const fromDefault = WatkinsLocalizationMarket.getCustomerDefaultAddressMarketIso();
    if (fromDefault === 'US' || fromDefault === 'CA') return fromDefault;
    return null;
  }

  /**
   * Lock picker only when logged in AND default address is US or CA.
   * No default address → shopper may switch markets freely (same as guest).
   * @returns {boolean}
   */
  static shouldLockMarketPicker() {
    const associated = WatkinsLocalizationMarket.getLoggedInAssociatedMarketIso();
    return associated === 'US' || associated === 'CA';
  }

  /**
   * Hide non-matching US/CA rows when the market picker is locked.
   * @returns {boolean}
   */
  static shouldNarrowUsCaMarketRows() {
    return WatkinsLocalizationMarket.shouldLockMarketPicker();
  }

  /**
   * Guests / logged-in without default address: URL country → stored choice → consultant → IP.
   * Logged in with default address US/CA: that country only.
   * @returns {Promise<'US' | 'CA' | null>}
   */
  static async resolveUsCaMarketForFilter() {
    const loggedIn = typeof globalThis !== 'undefined' && globalThis.__watkinsCustomerLoggedIn === true;
    const fromDefault = loggedIn ? WatkinsLocalizationMarket.getCustomerDefaultAddressMarketIso() : null;

    if (fromDefault === 'US' || fromDefault === 'CA') {
      WatkinsLocalizationMarket.clearMarketUserOverride();
      return fromDefault;
    }

    // Explicit URL market (`?country=US` / `?country=CA`) — set picker flag (USD/CAD) + storage.
    const fromUrl = WatkinsLocalizationMarket.getCountryFromUrlQuery();
    if (fromUrl === 'US' || fromUrl === 'CA') {
      WatkinsLocalizationMarket.setMarketUserOverride();
      WatkinsLocalizationMarket.setWatkinsCountryInStorage(fromUrl);
      return fromUrl;
    }

    // Guest, or logged-in with no default address: allow free switching.
    if (WatkinsLocalizationMarket.guestMarketUserOverrideActive()) {
      const stored = WatkinsLocalizationMarket.getWatkinsCountryFromStorage();
      if (stored === 'US' || stored === 'CA') return stored;
    }

    if (WatkinsLocalizationMarket.isConsultantUrlSource() && WatkinsLocalizationMarket.consultantUrlMarketUserOverrideActive()) {
      const stored = WatkinsLocalizationMarket.getWatkinsCountryFromStorage();
      if (stored === 'US' || stored === 'CA') return stored;
    }

    const fromConsultant = WatkinsLocalizationMarket.getStoredConsultantMarketIso();
    if (fromConsultant) return fromConsultant;

    const storedGuest = WatkinsLocalizationMarket.getWatkinsCountryFromStorage();
    if (storedGuest === 'US' || storedGuest === 'CA') {
      try {
        const serverIso = String(
          document.querySelector(`${WatkinsLocalizationConfig.SELECTOR_COUNTRY_RADIOS}:checked`)?.value || ''
        ).toUpperCase();
        if (serverIso === storedGuest) return storedGuest;
      } catch {
        /* */
      }
    }

    const handle = await WatkinsLocalizationMarket.fetchBrowsingContextCountryHandle();
    return WatkinsLocalizationMarket.mapCountryToUsCaOrAll(handle);
  }

  /**
   * @returns {Promise<string|null>} country handle, e.g. US, CA, GB
   */
  static fetchBrowsingContextCountryHandle() {
    if (WatkinsLocalizationMarket._browsingContextCountryPromise) {
      return WatkinsLocalizationMarket._browsingContextCountryPromise;
    }
    WatkinsLocalizationMarket._browsingContextCountryPromise = (async () => {
      try {
        const root =
          (globalThis.Shopify &&
            globalThis.Shopify.routes &&
            typeof globalThis.Shopify.routes.root === 'string' &&
            globalThis.Shopify.routes.root) ||
          '/';
        const normalized = root.endsWith('/') ? root : `${root}/`;
        const rel = `${normalized}browsing_context_suggestions.json`;
        const url = new URL(rel, globalThis.location?.href || globalThis.location?.origin).href;
        const res = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
        if (!res.ok) return null;
        const data = await res.json();
        const h1 = data?.detected_values?.country?.handle;
        if (h1 != null && String(h1).trim() !== '') return String(h1).trim();
        const h2 = data?.suggestions?.[0]?.parts?.country?.handle;
        if (h2 != null && String(h2).trim() !== '') return String(h2).trim();
        return null;
      } catch {
        return null;
      }
    })();
    return WatkinsLocalizationMarket._browsingContextCountryPromise;
  }
}

/**
 * `<localization-form>`: market radios, custom-select sync, and `/localization` return_to.
 * @extends HTMLElement
 */
class LocalizationForm extends HTMLElement {
  constructor() {
    super();
    this._onFormChange = this._onFormChange.bind(this);
    this._onConsultantStorageUpdate = () => {
      queueMicrotask(() => {
        void this._applyMarketFilterFromSources();
      });
    };
  }

  /**
   * Caches form and custom-select references. Must run after connect (children exist).
   */
  _syncDomRefs() {
    this._form = Utility.DomSelector.$(WatkinsLocalizationConfig.SELECTOR_FORM, this);
    this._customSelect = Utility.DomSelector.$(WatkinsLocalizationConfig.SELECTOR_CUSTOM_SELECT, this);
    this._formRadios = this._form ? Utility.DomSelector.$$('input[type="radio"]', this._form) : [];
  }

  /**
   * Redirect target after /localization: same page path + query with `country` = selected ISO (e.g. CA, US).
   * @param {string} countryIso
   */
  _setReturnToWithCountryParam(countryIso) {
    const form = this._form;
    if (!form || !countryIso) return;
    let returnInput = form.querySelector('input[name="return_to"]');
    if (!returnInput) {
      returnInput = document.createElement('input');
      returnInput.type = 'hidden';
      returnInput.name = 'return_to';
      form.appendChild(returnInput);
    }
    try {
      const next = new URL(window.location.href);
      next.searchParams.set('country', String(countryIso).toUpperCase());
      returnInput.value = `${next.pathname}${next.search}${next.hash}`;
    } catch {
      returnInput.value = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    }
  }

  /**
   * Keeps toggle label + li.selected in sync with the radio (so UI matches after redirect and if listener order varies).
   * @param {HTMLInputElement} countryInput
   */
  _syncCustomSelectToCountryInput(countryInput) {
    this._syncDomRefs();
    if (!this._customSelect || !countryInput || countryInput.name !== 'country_code') return;

    const radioGroup = this._form?.querySelectorAll(WatkinsLocalizationConfig.SELECTOR_COUNTRY_RADIOS);
    radioGroup?.forEach((inp) => {
      const row = inp.closest('li');
      if (!row) return;
      row.classList.toggle('selected', inp === countryInput);
    });

    const li = countryInput.closest('li');
    const label = li?.querySelector('label');
    const toggle = this._customSelect.querySelector(WatkinsLocalizationConfig.SELECTOR_CUSTOM_SELECT_TOGGLE);
    const optionText = toggle?.querySelector(WatkinsLocalizationConfig.SELECTOR_OPTION_LABEL_TEXT);
    if (label && optionText) {
      optionText.textContent = label.textContent;
      WatkinsLocalizationDom.syncCountryDetectClassesOnOptionText(optionText, countryInput.value);
    }
  }

  /**
   * Syncs the country radios + toggle to `watkins_country` on load (no form submit).
   * Skips if the option is missing or hidden (e.g. consultant market filter).
   */
  _applyWatkinsCountryFromStorage() {
    this._syncDomRefs();
    if (!this._form) return;
    const storedIso = WatkinsLocalizationMarket.getWatkinsCountryFromStorage();
    if (!storedIso) return;

    const targetInput = this._form.querySelector(
      `${WatkinsLocalizationConfig.SELECTOR_COUNTRY_RADIOS}[value="${CSS.escape(storedIso)}"]`
    );
    if (!targetInput) return;
    const row = targetInput.closest('li');
    if (!row || row.classList.contains(WatkinsLocalizationConfig.CLASS_COUNTRY_ROW_HIDDEN)) return;

    if (targetInput.checked) {
      this._syncCustomSelectToCountryInput(targetInput);
      return;
    }
    this._form.querySelectorAll(WatkinsLocalizationConfig.SELECTOR_COUNTRY_RADIOS).forEach((inp) => {
      inp.checked = false;
    });
    targetInput.checked = true;
    this._syncCustomSelectToCountryInput(targetInput);
  }

  /**
   * @param {'US' | 'CA' | null} targetIso null = show every country option (e.g. non US/CA visitor)
   */
  _applyUsCaRowFilter(targetIso) {
    this._syncDomRefs();
    if (!this._form) return;

    const countryInputs = this._form.querySelectorAll(WatkinsLocalizationConfig.SELECTOR_COUNTRY_RADIOS);
    if (!countryInputs.length) return;

    const associatedIso = WatkinsLocalizationMarket.getLoggedInAssociatedMarketIso();
    const lockPicker = WatkinsLocalizationMarket.shouldLockMarketPicker();
    // Locked only when default address is US/CA — then force that market and hide the other option.
    const effectiveIso = lockPicker ? associatedIso : targetIso;
    const shouldNarrow = lockPicker && (effectiveIso === 'US' || effectiveIso === 'CA');
    const showAllMarkets = !shouldNarrow;
    const displayIso = shouldNarrow ? effectiveIso : targetIso;

    WatkinsLocalizationDom.setMarketPickerLocked(this, lockPicker);

    countryInputs.forEach((input) => {
      const listItem = input.closest('li');
      if (!listItem) return;
      const isMatch = !showAllMarkets && input.value === displayIso;
      WatkinsLocalizationDom.setCountryListItemVisible(listItem, showAllMarkets || isMatch);
    });

    if (showAllMarkets) {
      WatkinsLocalizationDom.setMarketPickerLocked(this, false);
      return;
    }

    const targetInput = this._form.querySelector(
      `${WatkinsLocalizationConfig.SELECTOR_COUNTRY_RADIOS}[value="${CSS.escape(displayIso)}"]`
    );
    if (!targetInput || !this._customSelect) return;

    const toggle = this._customSelect.querySelector(WatkinsLocalizationConfig.SELECTOR_CUSTOM_SELECT_TOGGLE);
    const optionLabel = toggle?.querySelector(WatkinsLocalizationConfig.SELECTOR_OPTION_LABEL_TEXT);

    if (!targetInput.checked) {
      countryInputs.forEach((inp) => {
        inp.checked = false;
      });
      targetInput.checked = true;
      const iso = targetInput.value;
      WatkinsLocalizationMarket.setWatkinsCountryInStorage(iso);
      this._setReturnToWithCountryParam(iso);
      this._syncCustomSelectToCountryInput(targetInput);
      if (!WatkinsLocalizationMarket.hasAutoSubmittedMarketMisfit && !WatkinsLocalizationMarket.pageHasDynamicCheckoutButton()) {
        WatkinsLocalizationMarket.hasAutoSubmittedMarketMisfit = true;
        const form = this._form;
        setTimeout(() => {
          WatkinsLocalizationMarket.submitMarketFormPreservingCart(form);
        }, 100);
      }
      return;
    }

    // Radio already matches, but Shopify market/URL may still be wrong — force one POST.
    this._ensureLoggedInCustomerMarketSynced(displayIso);

    const selectedLi = targetInput.closest('li');
    countryInputs.forEach((i) => i.closest('li')?.classList.remove('selected'));
    selectedLi?.classList.add('selected');
    const label = selectedLi?.querySelector('label');
    if (label && optionLabel) {
      optionLabel.textContent = label.textContent;
      WatkinsLocalizationDom.syncCountryDetectClassesOnOptionText(optionLabel, targetInput.value);
    }
  }

  /**
   * Logged-in with US/CA default address: keep Shopify market in sync via `/localization`.
   * @param {'US' | 'CA' | null} targetIso
   */
  _ensureLoggedInCustomerMarketSynced(targetIso) {
    if (targetIso !== 'US' && targetIso !== 'CA') return;
    if (!WatkinsLocalizationMarket.shouldLockMarketPicker()) return;

    this._syncDomRefs();
    if (!this._form) return;

    WatkinsLocalizationMarket.setWatkinsCountryInStorage(targetIso);
    this._applyWatkinsCountryFromStorage();

    let urlCountry = '';
    try {
      urlCountry = String(new URL(window.location.href).searchParams.get('country') || '').toUpperCase();
    } catch {
      urlCountry = '';
    }

    const checked = this._form.querySelector(`${WatkinsLocalizationConfig.SELECTOR_COUNTRY_RADIOS}:checked`);
    const current = checked instanceof HTMLInputElement ? checked.value : '';
    const alreadyOnMarket = current === targetIso && (urlCountry === '' || urlCountry === targetIso);
    if (alreadyOnMarket) {
      if (checked instanceof HTMLInputElement) {
        this._syncCustomSelectToCountryInput(checked);
      }
      this._setReturnToWithCountryParam(targetIso);
      return;
    }

    const targetInput = this._form.querySelector(
      `${WatkinsLocalizationConfig.SELECTOR_COUNTRY_RADIOS}[value="${CSS.escape(targetIso)}"]`
    );
    if (!(targetInput instanceof HTMLInputElement)) return;

    this._form.querySelectorAll(WatkinsLocalizationConfig.SELECTOR_COUNTRY_RADIOS).forEach((inp) => {
      if (inp instanceof HTMLInputElement) inp.checked = false;
    });
    targetInput.checked = true;
    this._setReturnToWithCountryParam(targetIso);
    this._syncCustomSelectToCountryInput(targetInput);

    if (!WatkinsLocalizationMarket.hasAutoSubmittedMarketMisfit && !WatkinsLocalizationMarket.pageHasDynamicCheckoutButton()) {
      WatkinsLocalizationMarket.hasAutoSubmittedMarketMisfit = true;
      const form = this._form;
      setTimeout(() => {
        WatkinsLocalizationMarket.submitMarketFormPreservingCart(form);
      }, 100);
    }
  }

  /**
   * Consultant link flow: once `watkins_consultant` resolves to US/CA and matches `targetIso`, keep
   * `watkins_country`, radios, custom-select label, and `return_to` in sync; POST `/localization` once if
   * HTML selection still mismatches (e.g. Shopify default country). Skipped when only logged-in consultants
   * use the narrow one-row UI (`_applyUsCaRowFilter` handles that).
   * @param {'US' | 'CA' | null} targetIso
   */
  _ensureConsultantUrlMarketSynced(targetIso) {
    if (targetIso !== 'US' && targetIso !== 'CA') return;
    if (!WatkinsLocalizationMarket.isConsultantUrlShoppingContext()) return;
    if (WatkinsLocalizationMarket.consultantUrlMarketUserOverrideActive()) return;
    if (WatkinsLocalizationMarket.shouldNarrowUsCaMarketRows()) return;

    const fromConsultant = WatkinsLocalizationMarket.getStoredConsultantMarketIso();
    if (fromConsultant !== targetIso) return;

    this._syncDomRefs();
    if (!this._form) return;

    WatkinsLocalizationMarket.setWatkinsCountryInStorage(targetIso);
    this._applyWatkinsCountryFromStorage();

    const checked = this._form.querySelector(`${WatkinsLocalizationConfig.SELECTOR_COUNTRY_RADIOS}:checked`);
    const current = checked instanceof HTMLInputElement ? checked.value : '';

    if (current === targetIso) {
      if (checked instanceof HTMLInputElement) {
        this._syncCustomSelectToCountryInput(checked);
      }
      this._setReturnToWithCountryParam(targetIso);
      return;
    }

    const targetInput = this._form.querySelector(
      `${WatkinsLocalizationConfig.SELECTOR_COUNTRY_RADIOS}[value="${CSS.escape(targetIso)}"]`
    );
    if (!(targetInput instanceof HTMLInputElement)) return;

    const row = targetInput.closest('li');
    if (!row || row.classList.contains(WatkinsLocalizationConfig.CLASS_COUNTRY_ROW_HIDDEN)) return;

    this._form.querySelectorAll(WatkinsLocalizationConfig.SELECTOR_COUNTRY_RADIOS).forEach((inp) => {
      if (inp instanceof HTMLInputElement) inp.checked = false;
    });
    targetInput.checked = true;
    this._setReturnToWithCountryParam(targetIso);
    this._syncCustomSelectToCountryInput(targetInput);

    if (!WatkinsLocalizationMarket.hasAutoSubmittedMarketMisfit && !WatkinsLocalizationMarket.pageHasDynamicCheckoutButton()) {
      WatkinsLocalizationMarket.hasAutoSubmittedMarketMisfit = true;
      const form = this._form;
      setTimeout(() => {
        WatkinsLocalizationMarket.submitMarketFormPreservingCart(form);
      }, 100);
    }
  }

  /**
   * URL `?country=US|CA`: keep radios, custom-language-picker (USD/CAD), and Shopify market in sync.
   * Posts `/localization` once when the selected market does not match the URL country.
   * @param {'US' | 'CA' | null} targetIso
   */
  _ensureUrlQueryMarketSynced(targetIso) {
    if (targetIso !== 'US' && targetIso !== 'CA') return;
    const fromUrl = WatkinsLocalizationMarket.getCountryFromUrlQuery();
    if (fromUrl !== targetIso) return;
    if (WatkinsLocalizationMarket.shouldLockMarketPicker()) return;

    this._syncDomRefs();
    if (!this._form) return;

    WatkinsLocalizationMarket.setMarketUserOverride();
    WatkinsLocalizationMarket.setWatkinsCountryInStorage(targetIso);
    this._applyWatkinsCountryFromStorage();

    const checked = this._form.querySelector(`${WatkinsLocalizationConfig.SELECTOR_COUNTRY_RADIOS}:checked`);
    const current = checked instanceof HTMLInputElement ? checked.value : '';

    if (current === targetIso) {
      if (checked instanceof HTMLInputElement) {
        this._syncCustomSelectToCountryInput(checked);
      }
      this._setReturnToWithCountryParam(targetIso);
      // Still POST if Shopify market country differs from URL (e.g. landed with ?country=CA on US market).
      let shopifyCountry = '';
      try {
        shopifyCountry = String(globalThis.Shopify?.country || '').trim().toUpperCase();
      } catch {
        shopifyCountry = '';
      }
      const shopifyMapped = WatkinsLocalizationMarket.mapCountryToUsCaOrAll(shopifyCountry);
      if (shopifyMapped == null || shopifyMapped === targetIso) {
        return;
      }
    }

    const targetInput = this._form.querySelector(
      `${WatkinsLocalizationConfig.SELECTOR_COUNTRY_RADIOS}[value="${CSS.escape(targetIso)}"]`
    );
    if (!(targetInput instanceof HTMLInputElement)) return;

    const row = targetInput.closest('li');
    if (!row || row.classList.contains(WatkinsLocalizationConfig.CLASS_COUNTRY_ROW_HIDDEN)) return;

    this._form.querySelectorAll(WatkinsLocalizationConfig.SELECTOR_COUNTRY_RADIOS).forEach((inp) => {
      if (inp instanceof HTMLInputElement) inp.checked = false;
    });
    targetInput.checked = true;
    this._setReturnToWithCountryParam(targetIso);
    this._syncCustomSelectToCountryInput(targetInput);

    if (!WatkinsLocalizationMarket.hasAutoSubmittedMarketMisfit && !WatkinsLocalizationMarket.pageHasDynamicCheckoutButton()) {
      WatkinsLocalizationMarket.hasAutoSubmittedMarketMisfit = true;
      const form = this._form;
      setTimeout(() => {
        WatkinsLocalizationMarket.submitMarketFormPreservingCart(form);
      }, 100);
    }
  }

  async _applyMarketFilterFromSources() {
    this._syncDomRefs();
    try {
      if (!this._form) return;
      const countryInputs = this._form.querySelectorAll(WatkinsLocalizationConfig.SELECTOR_COUNTRY_RADIOS);
      if (!countryInputs.length) return;

      try {
        const src = globalThis.localStorage.getItem(WatkinsLocalizationConfig.STORAGE_KEY_WATKINS_CONSULTANT_SOURCE);
        const cid = globalThis.localStorage.getItem(WatkinsLocalizationConfig.STORAGE_KEY_WATKINS_CONSULTANT_ID);
        const cidStr = cid != null ? String(cid).trim() : '';
        if (src === 'url' && cidStr) {
          let prev = '';
          try {
            prev =
              globalThis.sessionStorage.getItem(WatkinsLocalizationConfig.SESSION_KEY_LAST_CONSULTANT_ID_FOR_MARKET) ||
              '';
          } catch {
            /* */
          }
          if (prev !== '' && prev !== cidStr) {
            try {
              globalThis.localStorage.removeItem(WatkinsLocalizationConfig.STORAGE_KEY_WATKINS_COUNTRY_USER_OVERRIDE);
            } catch {
              /* */
            }
          }
          try {
            globalThis.sessionStorage.setItem(
              WatkinsLocalizationConfig.SESSION_KEY_LAST_CONSULTANT_ID_FOR_MARKET,
              cidStr
            );
          } catch {
            /* */
          }
        }
      } catch {
        /* */
      }

      const targetIso = await WatkinsLocalizationMarket.resolveUsCaMarketForFilter();
      const fromDefault = WatkinsLocalizationMarket.getCustomerDefaultAddressMarketIso();
      const displayIso = fromDefault === 'US' || fromDefault === 'CA' ? fromDefault : targetIso;
      if (
        (WatkinsLocalizationConfig.DEBUG_ALERT_RESOLVED_COUNTRY || globalThis.__watkinsDebugCountry) &&
        !WatkinsLocalizationMarket._hasShownWatkinsCountryDebugAlert
      ) {
        WatkinsLocalizationMarket._hasShownWatkinsCountryDebugAlert = true;
        try {
          const consultantIso = WatkinsLocalizationMarket.getStoredConsultantMarketIso();
          const s = (v) => (v == null || v === '' ? '—' : String(v));
          globalThis.alert(
            `Watkins market (US/CA row filter)\n\n` +
              `Resolved: ${s(targetIso) || 'null = show both US+CA'}\n` +
              `Display: ${s(displayIso)}\n` +
              `Default address market: ${s(fromDefault)}\n` +
              `Consultant: ${s(consultantIso)}\n` +
              `Default address country: ${s(globalThis.__watkinsCustomerCountryIso)}\n` +
              `Has default address: ${String(!!globalThis.__watkinsCustomerHasDefaultAddress)}\n` +
              `Order country: ${s(globalThis.__watkinsCustomerOrderCountryIso)}\n` +
              `Has orders: ${String(!!globalThis.__watkinsCustomerHasOrders)}\n` +
              `Logged in: ${String(!!globalThis.__watkinsCustomerLoggedIn)}\n` +
              `Picker locked: ${String(WatkinsLocalizationMarket.shouldLockMarketPicker())}\n` +
              `watkins_country: ${s(
                (() => {
                  try {
                    return globalThis.localStorage.getItem(WatkinsLocalizationConfig.STORAGE_KEY_WATKINS_COUNTRY);
                  } catch {
                    return null;
                  }
                })()
              )}`
          );
        } catch {
          /* */
        }
      }
      if (displayIso === 'US' || displayIso === 'CA') {
        // Guests with a manual override already have the correct storage value from resolve.
        if (
          WatkinsLocalizationMarket.shouldLockMarketPicker() ||
          !WatkinsLocalizationMarket.guestMarketUserOverrideActive()
        ) {
          WatkinsLocalizationMarket.setWatkinsCountryInStorage(displayIso);
        }
      }
      this._applyUsCaRowFilter(displayIso);
      this._applyWatkinsCountryFromStorage();
      if (!WatkinsLocalizationMarket.shouldLockMarketPicker()) {
        this._ensureUrlQueryMarketSynced(displayIso);
        this._ensureConsultantUrlMarketSynced(displayIso);
      }
      this._ensureLoggedInCustomerMarketSynced(displayIso);
      const checkedCountry = this._form.querySelector(`${WatkinsLocalizationConfig.SELECTOR_COUNTRY_RADIOS}:checked`);
      if (checkedCountry) {
        this._syncCustomSelectToCountryInput(/** @type {HTMLInputElement} */ (checkedCountry));
      }
    } finally {
      WatkinsLocalizationDom.setCurrencyDropdownMarketUiReady(this);
    }
  }

  connectedCallback() {
    this._syncDomRefs();
    if (!this._form || !this._customSelect) return;
    if (this._isBound) return;
    /** URL `?country=` first so USD/CAD flag matches before async IP/consultant resolve. */
    const urlIso = WatkinsLocalizationMarket.getCountryFromUrlQuery();
    if (
      (urlIso === 'US' || urlIso === 'CA') &&
      !WatkinsLocalizationMarket.shouldLockMarketPicker()
    ) {
      WatkinsLocalizationMarket.setMarketUserOverride();
      WatkinsLocalizationMarket.setWatkinsCountryInStorage(urlIso);
    }
    /** Align toggle with `watkins_country` before async fetch to reduce label/flag swap. */
    this._applyWatkinsCountryFromStorage();
    this._isBound = true;

    this._formRadios.forEach((input) => input.addEventListener('change', this._onFormChange));
    document.addEventListener(WatkinsLocalizationConfig.EVENT_WATKINS_CONSULTANT_STORAGE, this._onConsultantStorageUpdate);
    void this._applyMarketFilterFromSources();
  }

  disconnectedCallback() {
    if (!this._isBound) return;
    this._isBound = false;
    this._syncDomRefs();
    this._formRadios?.forEach((input) => input.removeEventListener('change', this._onFormChange));
    document.removeEventListener(
      WatkinsLocalizationConfig.EVENT_WATKINS_CONSULTANT_STORAGE,
      this._onConsultantStorageUpdate
    );
  }

  _onFormChange(event) {
    const target = event.target;
    if (WatkinsLocalizationMarket.shouldLockMarketPicker()) {
      event.preventDefault();
      event.stopPropagation();
      // Re-sync to associated market if something tried to change the radio.
      void this._applyMarketFilterFromSources();
      return;
    }
    if (target && target.getAttribute('name') === 'country_code' && target.classList.contains('country-option')) {
      const row = target.closest('li');
      if (row?.classList.contains(WatkinsLocalizationConfig.CLASS_COUNTRY_ROW_HIDDEN)) {
        event.preventDefault();
        return;
      }
      const iso = /** @type {HTMLInputElement} */ (target).value;
      // Guest (and consultant-url) manual pick: keep choice across pages / IP re-resolve.
      WatkinsLocalizationMarket.setMarketUserOverride();
      WatkinsLocalizationMarket.setWatkinsCountryInStorage(iso);
      this._setReturnToWithCountryParam(iso);
      this._syncCustomSelectToCountryInput(/** @type {HTMLInputElement} */ (target));
    }
    event.preventDefault();
    setTimeout(() => {
      this._form?.submit();
    }, 100);
  }
}

customElements.define('localization-form', LocalizationForm);

// Re-adds any cart contents a prior page load's add-to-cart (or the
// automatic market-switch reload, via submitMarketFormPreservingCart above)
// stashed via Utility.Cart.rememberAddedItems() but that are missing from
// the cart on this load — the single general restore path for every cause
// of a cart-emptying reload, not just the market switch.
globalThis.Utility?.Cart?.restoreMissingCartAdditions?.();

WatkinsLocalizationCountryContent.bootDetectedCountryContent();

globalThis.getActiveWatkinsCountryForDetectedContent =
  WatkinsLocalizationCountryContent.getActiveWatkinsCountryForDetectedContent;
globalThis.applyWatkinsDetectedCountryContent = WatkinsLocalizationCountryContent.applyDetectedCountryContent;
globalThis.syncAllSubmittedFromFieldsFromWatkinsCountry =
  WatkinsLocalizationCountryContent.syncAllSubmittedFromFieldsFromWatkinsCountry;
